import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface SelectionStore {
  // State
  selectedObjectIds: string[];
  hoveredObjectId: string | null;
  transformMode: 'translate' | 'rotate' | 'scale';
  transformSpace: 'world' | 'local';
  multiSelectMode: boolean;

  // Selection operations
  selectObject: (objectId: string, multi?: boolean) => void;
  selectObjects: (objectIds: string[]) => void;
  deselectObject: (objectId: string) => void;
  deselectAll: () => void;
  toggleSelection: (objectId: string) => void;
  selectAll: () => void;

  // Hover operations
  setHovered: (objectId: string | null) => void;

  // Transform operations
  setTransformMode: (mode: 'translate' | 'rotate' | 'scale') => void;
  setTransformSpace: (space: 'world' | 'local') => void;

  // Multi-select operations
  setMultiSelectMode: (enabled: boolean) => void;

  // Utilities
  isSelected: (objectId: string) => boolean;
  isHovered: (objectId: string) => boolean;
  hasSelection: () => boolean;
  getSelectedCount: () => number;
  getFirstSelected: () => string | null;
  getLastSelected: () => string | null;
}

export const useSelectionStore = create<SelectionStore>()(
  immer((set, get) => ({
    // Initial state
    selectedObjectIds: [],
    hoveredObjectId: null,
    transformMode: 'translate',
    transformSpace: 'world',
    multiSelectMode: false,

    // Selection operations
    selectObject: (objectId, multi = false) => {
      set((state) => {
        if (multi || state.multiSelectMode) {
          if (!state.selectedObjectIds.includes(objectId)) {
            state.selectedObjectIds.push(objectId);
          }
        } else {
          state.selectedObjectIds = [objectId];
        }
      });
    },

    selectObjects: (objectIds) => {
      set((state) => {
        state.selectedObjectIds = [...objectIds];
      });
    },

    deselectObject: (objectId) => {
      set((state) => {
        state.selectedObjectIds = state.selectedObjectIds.filter((id) => id !== objectId);
      });
    },

    deselectAll: () => {
      set((state) => {
        state.selectedObjectIds = [];
      });
    },

    toggleSelection: (objectId) => {
      const { selectedObjectIds } = get();
      if (selectedObjectIds.includes(objectId)) {
        get().deselectObject(objectId);
      } else {
        get().selectObject(objectId, true);
      }
    },

    selectAll: () => {
      // This would need to be implemented with scene store integration
      // For now, we'll leave it as a placeholder
    },

    // Hover operations
    setHovered: (objectId) => {
      set((state) => {
        state.hoveredObjectId = objectId;
      });
    },

    // Transform operations
    setTransformMode: (mode) => {
      set((state) => {
        state.transformMode = mode;
      });
    },

    setTransformSpace: (space) => {
      set((state) => {
        state.transformSpace = space;
      });
    },

    // Multi-select operations
    setMultiSelectMode: (enabled) => {
      set((state) => {
        state.multiSelectMode = enabled;
      });
    },

    // Utilities
    isSelected: (objectId) => {
      const { selectedObjectIds } = get();
      return selectedObjectIds.includes(objectId);
    },

    isHovered: (objectId) => {
      const { hoveredObjectId } = get();
      return hoveredObjectId === objectId;
    },

    hasSelection: () => {
      const { selectedObjectIds } = get();
      return selectedObjectIds.length > 0;
    },

    getSelectedCount: () => {
      const { selectedObjectIds } = get();
      return selectedObjectIds.length;
    },

    getFirstSelected: () => {
      const { selectedObjectIds } = get();
      return selectedObjectIds.length > 0 ? selectedObjectIds[0] : null;
    },

    getLastSelected: () => {
      const { selectedObjectIds } = get();
      return selectedObjectIds.length > 0 ? selectedObjectIds[selectedObjectIds.length - 1] : null;
    },
  })),
);
